// Pantalla del chatroom individual (versión web).
// Muestra mensajes entre el usuario autenticado y otro usuario (friend), input para enviar, opciones.
// Usa hooks para mensajes y envío.
// Adaptado para web desde amarsi-app (React Native a React DOM).

import React, { useEffect, useState, useRef, useCallback, useMemo, memo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
// import { useQueryClient } from '@tanstack/react-query'; // Comentado - canal directo desactivado
import { useChatMessages } from '../../hooks/chat/useChatMessages';
import { useSendMessage } from '../../hooks/chat/useSendMessage';
import { useMessageSounds } from '../../hooks/chat/useMessageSounds';
import { useBatchMarkAsRead } from '../../hooks/chat/useBatchMarkAsRead';
import { useBlockDetection } from '../../hooks/useBlockDetection';
import { useBlockUserMutation } from '../../hooks/useFriendMutations';
import MemoizedMessageGroup from '../../components/chat/MemoizedMessageGroup';
import InputChatBox from '../../components/chat/InputChatBox';
import { useAuthQuery } from '../../hooks/useAuthQuery';
import { FiArrowLeft, FiMoreVertical, FiPaperclip, FiX } from 'react-icons/fi';
import { BiLoaderAlt } from "react-icons/bi";
import { defaultImage } from '../../constants/defaultImage';
import { chatService } from '../../services/chatService';
import PermanentAlbums from '../../components/album-sistem/my-albums/PermanentAlbums';
// import { supabase } from '../../lib/supabase'; // Comentado - canal directo desactivado

// Constante para controlar logs de desarrollo
const DEBUG = process.env.NODE_ENV === 'development';

function ChatRoomScreen() {
  // Obtiene estado de navegación (no usa parámetros de URL por seguridad)
  const navigate = useNavigate();
  const location = useLocation();
  const navigationState = location.state || {};

  // Memoiza datos extraídos del state para evitar re-renders
  // Usamos valores individuales en lugar de todo el objeto navigationState
  const chatroom_id = navigationState?.chatroom_id;
  const friendId = navigationState?.friend_id;
  const rawAvatar = navigationState?.avatar;

  // Memoiza valores individuales para evitar re-renders
  const memoizedChatroomId = useMemo(() => chatroom_id, [chatroom_id]);
  const memoizedFriendId = useMemo(() => friendId, [friendId]);
  const avatar = useMemo(() => rawAvatar || defaultImage, [rawAvatar]);

  // Memoiza destinatario para evitar cambios en cada render
  // Incluye tanto friend_id como id para compatibilidad con diferentes componentes
  const destinatario = useMemo(() => {
    const baseDestinatario = navigationState?.destinatario || { full_name: 'Usuario' };
    return {
      ...baseDestinatario,
      friend_id: baseDestinatario.friend_id || friendId,
      id: baseDestinatario.id || baseDestinatario.friend_id || friendId,
    };
  }, [navigationState?.destinatario, friendId]);

  // Obtiene la sesión del usuario
  const { sessionQuery } = useAuthQuery();
  const session = sessionQuery.data?.session;
  const user = session?.user;
  const sessionLoading = sessionQuery.isLoading;

  // Obtiene el queryClient para invalidaciones manuales (comentado - canal directo desactivado)
  // const queryClient = useQueryClient();

  // Hook para manejo de sonidos de mensajes
  const { playSendSound, playReceiveSound } = useMessageSounds();

  // Hook para batch de mensajes leídos
  const { markAsRead: markAsReadRaw, flushBatch, cleanup } = useBatchMarkAsRead();
  
  // Usar useRef para estabilizar markAsRead completamente
  const markAsReadRef = useRef(markAsReadRaw);
  markAsReadRef.current = markAsReadRaw;
  
  // Función estable que siempre usa la referencia actual
  const markAsRead = useCallback((id) => markAsReadRef.current(id), []);

  // Hook para detección de bloqueo en tiempo real
  const { isBlocked } = useBlockDetection(friendId);

  // Referencias y estados
  const messagesContainerRef = useRef(null);
  const textInputRef = useRef(null);

  const [modalVisible, setModalVisible] = useState(false);
  const [modalFriendActionVisible, setModalFriendActionVisible] = useState(false);
  const [messageText, setMessageText] = useState('');
  const [selectionList, setSelectionList] = useState([]);
  const [respondTo, setRespondTo] = useState(null);

  // Estado para controlar qué pestaña del modal está activa
  const [activeModalTab, setActiveModalTab] = useState('location'); // 'location', 'private_album', 'shared_album'

  // Estado para checkbox de "Verse una sola vez" (por defecto false = unlimited)
  const [viewOnceEnabled, setViewOnceEnabled] = useState(false);

  // Estado para mensajes optimistas (mensajes enviados pero aún no confirmados por el servidor)
  const [optimisticMessages, setOptimisticMessages] = useState([]);

  // Estado para rastrear mensajes eliminados optimistamente (msg_type cambiado a 0)
  const [deletedMessageIds, setDeletedMessageIds] = useState(new Set());

  // Estado para modo de selección de imágenes en el chat
  const [selectionMode, setSelectionMode] = useState(false);
  const [selectedChatImages, setSelectedChatImages] = useState([]);

  // Hook para mensajes (solo se ejecuta cuando friendId es estable)
  const { data: messages, isLoading } = useChatMessages(memoizedFriendId);

  // Hook para enviar mensajes con manejo optimista
  const sendMessageMutation = useSendMessage();

  const blockMutation = useBlockUserMutation();

  // Referencias para controlar el estado de mensajes
  const isFirstRender = useRef(true);
  const lastMessageIds = useRef(new Set());
  const messagesReadRef = useRef(new Set()); // Track mensajes ya marcados como leídos

  // Función para scrollear al final de la lista
  const scrollBottom = useCallback(() => {
    if (DEBUG) console.log('scrollBottom: Scrolleando al final de la lista');
    if (messagesContainerRef.current) {
      messagesContainerRef.current.scrollTop = messagesContainerRef.current.scrollHeight;
    }
  }, []);

  // Objeto base para mensaje
  const mensajitoBase = useMemo(() => ({
    ahorita: new Date(),
    chatroom_id: chatroom_id,
    body: messageText,
    destinatario: friendId,
    msg_type: 1,
  }), [chatroom_id, messageText, friendId]);

  // Referencia para rastrear el último messages y evitar recálculos innecesarios
  const lastMessagesRef = useRef(null);

  // Combina mensajes del servidor con mensajes optimistas
  // Crea una estructura unificada para renderizado
  const combinedMessages = useMemo(() => {
    if (!messages) return [];

    // Verificar si messages realmente cambió (comparación por referencia)
    // Si es el mismo objeto, retornar el valor anterior para evitar recálculo
    if (messages === lastMessagesRef.current &&
        optimisticMessages.length === 0 &&
        deletedMessageIds.size === 0) {
      return lastMessagesRef.current.combinedMessages;
    }

    // Función auxiliar para obtener fecha local en formato YYYY-MM-DD
    const getLocalDateString = (date) => {
      const d = new Date(date);
      const year = d.getFullYear();
      const month = String(d.getMonth() + 1).padStart(2, '0');
      const day = String(d.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    };

    // Crear mapa de grupos existentes del servidor
    const serverGroups = new Map();
    messages.forEach(grupo => {
      // Aplicar msg_type 0 a mensajes que están en deletedMessageIds
      const mensajesConEliminados = grupo.mensajes_list.map(msg => ({
        ...msg,
        msg_type: deletedMessageIds.has(msg.id) ? 0 : msg.msg_type
      }));
      serverGroups.set(grupo.date_trunc, mensajesConEliminados);
    });

    // Agregar mensajes optimistas a los grupos correspondientes
    optimisticMessages.forEach(msg => {
      const dateStr = getLocalDateString(msg.created_at);
      if (!serverGroups.has(dateStr)) {
        serverGroups.set(dateStr, []);
      }
      serverGroups.get(dateStr).push(msg);
    });

    // Convertir mapa a array de grupos y ordenar por fecha
    const result = Array.from(serverGroups.entries()).map(([dateTrunc, mensajesList]) => ({
      date_trunc: dateTrunc,
      mensajes_list: mensajesList
    }));

    // Ordenar grupos por fecha (más antigua primero)
    result.sort((a, b) => new Date(a.date_trunc) - new Date(b.date_trunc));

    // Ordenar mensajes dentro de cada grupo por fecha
    result.forEach(grupo => {
      grupo.mensajes_list.sort((a, b) => {
        const dateA = new Date(a.created_at);
        const dateB = new Date(b.created_at);
        return dateA - dateB;
      });
    });

    // Guardar referencia
    lastMessagesRef.current = {
      messages,
      combinedMessages: result
    };

    return result;
  }, [messages, optimisticMessages, deletedMessageIds]);

  // Efecto para limpiar mensajes optimistas cuando el mensaje real del servidor llega
  // Esto evita el parpadeo manteniendo el mensaje optimista hasta que el real llega
  useEffect(() => {
    if (!messages || optimisticMessages.length === 0) return;

    // Crear un array plano de todos los mensajes del servidor
    const allServerMessages = messages.flatMap(grupo => grupo.mensajes_list);

    // Filtrar mensajes optimistas que ya tienen correspondiente en el servidor
    const filteredOptimisticMessages = optimisticMessages.filter(optMsg => {
      // Buscar si hay un mensaje del servidor que coincida
      for (const serverMsg of allServerMessages) {
        const serverTime = new Date(serverMsg.created_at).getTime();
        const optTime = new Date(optMsg.created_at).getTime();
        const timeDiff = Math.abs(serverTime - optTime);

        // Si encontramos un mensaje del servidor que coincide con el optimista
        if (
          serverMsg.body === optMsg.body &&
          serverMsg.remitente === optMsg.remitente &&
          serverMsg.destinatario === optMsg.destinatario &&
          serverMsg.msg_type === optMsg.msg_type &&
          timeDiff < 5000 // Dentro de 5 segundos
        ) {
          console.log('cleanupOptimisticMessages: Mensaje optimista reemplazado por mensaje del servidor:', optMsg.id);
          return false; // Eliminar este mensaje optimista
        }
      }
      return true; // Mantener este mensaje optimista
    });

    // Actualizar estado solo si hubo cambios
    if (filteredOptimisticMessages.length !== optimisticMessages.length) {
      if (DEBUG) console.log('cleanupOptimisticMessages: Limpiando', optimisticMessages.length - filteredOptimisticMessages.length, 'mensajes optimistas');
      setOptimisticMessages(filteredOptimisticMessages);
    }
  }, [messages, optimisticMessages]);

  // Efecto para limpiar deletedMessageIds cuando el servidor confirma que el mensaje tiene msg_type 0
  useEffect(() => {
    if (!messages || deletedMessageIds.size === 0) return;

    // Crear un array plano de todos los mensajes del servidor
    const allServerMessages = messages.flatMap(grupo => grupo.mensajes_list);

    // Filtrar IDs de mensajes que ya tienen msg_type 0 en el servidor
    const idsToRemove = [];
    deletedMessageIds.forEach(id => {
      const serverMsg = allServerMessages.find(msg => msg.id === id);
      if (serverMsg && serverMsg.msg_type === 0) {
        idsToRemove.push(id);
      }
    });

    // Actualizar estado solo si hay IDs para remover
    if (idsToRemove.length > 0) {
      if (DEBUG) console.log('cleanupDeletedMessageIds: Limpiando', idsToRemove.length, 'IDs de mensajes eliminados confirmados por servidor');
      setDeletedMessageIds(prev => {
        const newSet = new Set(prev);
        idsToRemove.forEach(id => newSet.delete(id));
        return newSet;
      });
    }
  }, [messages, deletedMessageIds]);

  // Función para enviar mensaje con optimistic update
  // Implementa optimistic update para mejorar UX: muestra el mensaje inmediatamente
  // antes de recibir confirmación del servidor.
  const sendMessage = useCallback(async (obj_Mensaje) => {
    if (DEBUG) console.log('sendMessage: Enviando mensaje:', obj_Mensaje);

    // Generar ID temporal para mensaje optimista
    const tempId = `temp_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

    // Preparar mensaje optimista con todos los campos necesarios
    const optimisticMessage = {
      id: tempId,
      body: obj_Mensaje.body,
      remitente: user?.id,
      destinatario: obj_Mensaje.destinatario,
      chatroom_id: obj_Mensaje.chatroom_id,
      msg_type: obj_Mensaje.msg_type,
      created_at: obj_Mensaje.ahorita || new Date().toISOString(),
      is_leido: false,
      is_optimistic: true, // Marcar como optimista para diferenciarlo
    };

    // Si es respuesta, agregar referencia y construir msg_extra_data
    if (respondTo) {
      obj_Mensaje.id_ref = respondTo.id;
      obj_Mensaje.msg_type = 4;
      optimisticMessage.id_ref = respondTo.id;
      optimisticMessage.msg_type = 4;
      
      // Construir msg_extra_data con la información del mensaje respondido
      // Esto permite que el componente MessageRespondTo muestre el contexto de la respuesta
      obj_Mensaje.msg_extra_data = {
        responded_to: {
          id: respondTo.id,
          body: respondTo.body,
          msg_type: respondTo.msg_type,
          created_at: respondTo.created_at,
          remitente: respondTo.remitente
        }
      };
      optimisticMessage.msg_extra_data = obj_Mensaje.msg_extra_data;
      
      if (DEBUG) console.log('sendMessage: Respondiendo a mensaje:', respondTo.id);
    }

    // Agregar mensaje optimista a la lista inmediatamente
    setOptimisticMessages(prev => [...prev, optimisticMessage]);

    // Limpiar input y estado de respuesta inmediatamente
    const currentMessageText = messageText;
    const currentRespondTo = respondTo;
    setMessageText('');
    setRespondTo(null);

    // Enviar mensaje al servidor
    sendMessageMutation.mutate(obj_Mensaje, {
      onSuccess: () => {
        if (DEBUG) console.log('sendMessage: Mensaje enviado exitosamente');
        // NO eliminamos el mensaje optimista aquí
        // El combinedMessages se encargará de eliminarlo cuando detecte el mensaje real del servidor
        // Esto evita el parpadeo (mensaje desaparece y reaparece)
        // Pequeño delay para asegurar que el mensaje se agregue a la lista antes de scrollear
        setTimeout(() => scrollBottom(), 200);
        playSendSound();
      },
      onError: (error) => {
        if (DEBUG) console.log('sendMessage: Error al enviar mensaje:', error);
        // Eliminar mensaje optimista de la lista
        setOptimisticMessages(prev => prev.filter(msg => msg.id !== tempId));
        // Restaurar el texto del input para que el usuario pueda reintentar
        setMessageText(currentMessageText);
        setRespondTo(currentRespondTo);
        // Mostrar alerta con el mensaje de error
        alert(error.message || 'Error al enviar mensaje. Inténtalo de nuevo.');
      },
    });
  }, [respondTo, sendMessageMutation, scrollBottom, playSendSound, user?.id, messageText]);

  // Función para enviar múltiples imágenes
  // Envía cada imagen seleccionada como mensaje separado con optimistic update.
  const multipleSendImage = useCallback(async () => {
    if (DEBUG) console.log('multipleSendImage: Enviando imágenes, cantidad:', selectionList.length);
    setModalVisible(false);

    // Generar IDs temporales para todos los mensajes optimistas
    const tempIds = selectionList.map(() => `temp_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`);

    // Crear y agregar mensajes optimistas para cada imagen
    selectionList.forEach((foto, index) => {
      const tempId = tempIds[index];
      const optimisticMessage = {
        id: tempId,
        body: 'Image',
        remitente: user?.id,
        destinatario: friendId,
        chatroom_id: chatroom_id,
        msg_type: 3,
        id_image_ref: foto.img_id,
        created_at: new Date().toISOString(),
        is_leido: false,
        is_optimistic: true,
      };
      setOptimisticMessages(prev => [...prev, optimisticMessage]);
    });

    const mensajitoConImagen = {
      ...mensajitoBase,
      body: 'Image',
      msg_type: 3,
    };

    const sendPromises = selectionList.map((foto, index) => {
      const msg = { ...mensajitoConImagen, id_image_ref: foto.img_id };
      const tempId = tempIds[index];
      if (DEBUG) console.log('multipleSendImage: Preparando envío de imagen:', foto.img_id);

      // Crear una promesa personalizada que maneje el éxito/error
      return new Promise((resolve, reject) => {
        sendMessageMutation.mutate(msg, {
          onSuccess: () => {
            if (DEBUG) console.log('multipleSendImage: Imagen enviada exitosamente:', foto.img_id);
            // NO eliminamos el mensaje optimista aquí
            // El combinedMessages se encargará de eliminarlo cuando detecte el mensaje real del servidor
            // Esto evita el parpadeo (mensaje desaparece y reaparece)
            resolve();
          },
          onError: (error) => {
            if (DEBUG) console.log('multipleSendImage: Error al enviar imagen:', foto.img_id, error);
            // Eliminar mensaje optimista
            setOptimisticMessages(prev => prev.filter(m => m.id !== tempId));
            // Mostrar alerta con el mensaje de error
            alert(error.message || 'Error al enviar imagen. Inténtalo de nuevo.');
            reject(error);
          },
        });
      });
    });

    try {
      await Promise.all(sendPromises);
      if (DEBUG) console.log('multipleSendImage: Todas las imágenes enviadas');
      setSelectionList([]); // Limpiar selección después de enviar exitosamente
    } catch (error) {
      if (DEBUG) console.error('multipleSendImage: Error al enviar algunas imágenes:', error);
      // No limpiamos la selección para que el usuario pueda reintentar
    }
  }, [selectionList, mensajitoBase, sendMessageMutation, user?.id, friendId, chatroom_id]);

  // Función para manejar la selección de imágenes (similar a AlbumManager)
  // Agrega o remueve imágenes de la lista de selección
  const imageArrayManager = useCallback((foto) => {
    if (DEBUG) console.log('imageArrayManager: Procesando imagen:', foto.img_id);
    const imgIdExists = selectionList.some(item => item.img_id === foto.img_id);
    if (imgIdExists) {
      if (DEBUG) console.log('imageArrayManager: Removiendo imagen de selección:', foto.img_id);
      setSelectionList(selectionList.filter(item => item.img_id !== foto.img_id));
    } else {
      if (DEBUG) console.log('imageArrayManager: Agregando imagen a selección:', foto.img_id);
      setSelectionList([...selectionList, foto]);
    }
    if (DEBUG) console.log('imageArrayManager: Total imágenes seleccionadas:', selectionList.length + (imgIdExists ? -1 : 1));
  }, [selectionList]);

  // Función para manejar la selección de imágenes desde el chat
  const handleChatImageSelect = useCallback((imageData) => {
    if (DEBUG) console.log('handleChatImageSelect: Procesando imagen del chat:', imageData.img_id);
    const imgIdExists = selectedChatImages.some(item => item.img_id === imageData.img_id || item.id === imageData.img_id);
    if (imgIdExists) {
      if (DEBUG) console.log('handleChatImageSelect: Removiendo imagen de selección del chat:', imageData.img_id);
      setSelectedChatImages(selectedChatImages.filter(item => item.img_id !== imageData.img_id && item.id !== imageData.img_id));
    } else {
      if (DEBUG) console.log('handleChatImageSelect: Agregando imagen a selección del chat:', imageData.img_id);
      setSelectedChatImages([...selectedChatImages, imageData]);
    }
  }, [selectedChatImages]);

  // Función para compartir imágenes con control de duración
  // Envía imágenes usando los nuevos métodos de chatService
  // Si viewOnceEnabled es true → usa sendEphemeralImage (msg_type 6)
  // Si viewOnceEnabled es false → usa sendPersistentImage (msg_type 3)
  const shareImagesWithDuration = useCallback(async () => {
    if (DEBUG) {
      console.log('=== shareImagesWithDuration: INICIO ===');
      console.log('shareImagesWithDuration: view_once:', viewOnceEnabled);
      console.log('shareImagesWithDuration: Imágenes a compartir:', selectionList.length);
      console.log('shareImagesWithDuration: IDs de imágenes:', selectionList.map(f => f.img_id));
    }
    setModalVisible(false);

    try {
      const sendPromises = selectionList.map(async (foto, index) => {
        if (DEBUG) console.log(`shareImagesWithDuration: Procesando imagen ${index + 1}/${selectionList.length}:`, foto.img_id);

        // Generar ID temporal para mensaje optimista
        const tempId = `temp_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
        const msgType = viewOnceEnabled ? 6 : 3;
        const body = viewOnceEnabled ? 'Imagen temporal' : 'Shared Image';

        // Crear mensaje optimista
        const optimisticMessage = {
          id: tempId,
          body: body,
          remitente: user?.id,
          destinatario: friendId,
          chatroom_id: chatroom_id,
          msg_type: msgType,
          created_at: new Date().toISOString(),
          is_leido: false,
          is_optimistic: true,
        };
        setOptimisticMessages(prev => [...prev, optimisticMessage]);

        // Enviar imagen usando el método apropiado
        try {
          if (viewOnceEnabled) {
            await chatService.sendEphemeralImage({
              chatroom_id,
              destinatario: friendId,
              image_id: foto.img_id,
              owner_id: user?.id
            });
          } else {
            await chatService.sendPersistentImage({
              chatroom_id,
              destinatario: friendId,
              image_id: foto.img_id,
              owner_id: user?.id
            });
          }
          if (DEBUG) console.log(`shareImagesWithDuration: ✅ Imagen ${index + 1} enviada exitosamente`);
        } catch (error) {
          if (DEBUG) console.error(`shareImagesWithDuration: ❌ Error al enviar imagen ${index + 1}:`, error);
          setOptimisticMessages(prev => prev.filter(m => m.id !== tempId));
          throw error;
        }
      });

      await Promise.all(sendPromises);
      if (DEBUG) {
        console.log('shareImagesWithDuration: ✅ Todas las imágenes enviadas exitosamente');
        console.log('=== shareImagesWithDuration: FIN EXITOSO ===');
      }
      setSelectionList([]);

    } catch (error) {
      if (DEBUG) {
        console.error('=== shareImagesWithDuration: ERROR ===');
        console.error('shareImagesWithDuration: Error al compartir imágenes:', error);
      }
      alert(error.message || 'Error al compartir imágenes. Inténtalo de nuevo.');
    }
  }, [selectionList, viewOnceEnabled, user?.id, friendId, chatroom_id]);

  // Función para enfocar input
  const focusearInput = useCallback(() => {
    if (DEBUG) console.log('focusearInput: Enfocando input de texto');
    if (textInputRef.current) {
      textInputRef.current.focus();
      if (DEBUG) console.log('focusearInput: Input enfocado');
    } else {
      if (DEBUG) console.log('focusearInput: textInputRef no disponible');
    }
  }, []);

  // Función para cerrar modal de acciones
  const handleCloseModal = useCallback(() => {
    setModalFriendActionVisible(false);
  }, []);

  // Función para manejar el bloqueo del usuario
  const handleBlockPerson = useCallback(async () => {
    try {
      if (DEBUG) {
        console.log('handleBlockPerson: Parámetros para bloqueo');
        console.log('- gestorId (user.id):', user?.id);
        console.log('- bloqueadoId (friendId):', friendId);
      }

      if (!user?.id || !friendId) {
        console.error('Error: IDs no válidos para bloqueo');
        alert('Error: información incompleta para bloquear usuario');
        return;
      }

      await blockMutation.mutateAsync({ gestorId: user.id, bloqueadoId: friendId });

      if (DEBUG) console.log('Usuario bloqueado exitosamente, navegando a Chats...');
      navigate('/chats');

    } catch (error) {
      console.error('Error bloqueando usuario:', error);
      alert(error.message || 'Error al bloquear usuario');
    }
  }, [user, friendId, navigate, blockMutation]);

  // Función para crear impeachment (reporte)
  // Navega a la pantalla de reporte pasando los datos del usuario objetivo por location.state
  const createImpeachment = useCallback(() => {
    if (DEBUG) console.log('createImpeachment: Navegando a pantalla de reporte');
    // Navegar a pantalla de reporte con los datos del usuario objetivo (sin exponer userId en URL)
    navigate('/report', { state: destinatario });
  }, [destinatario, navigate]);

  // Función para confirmar bloqueo de usuario
  const confirmBlockPerson = useCallback(() => {
    if (window.confirm(`¿Bloquear a ${destinatario?.full_name}? Ya no volveran a verse, hablar ni enviarse likes.`)) {
      handleBlockPerson();
    }
  }, [destinatario, handleBlockPerson]);

  // Memoizar función de ordenamiento para evitar cálculos repetidos
  const sortMessages = useCallback((mensajesList) => {
    return [...mensajesList].sort((a, b) => {
      const fechaA = new Date(a.ahorita || a.created_at);
      const fechaB = new Date(b.ahorita || b.created_at);
      return fechaA.getTime() - fechaB.getTime();
    });
  }, []);

  // Maneja la eliminación de un mensaje con optimistic update.
  // Agrega el ID del mensaje a deletedMessageIds para mostrarlo como eliminado inmediatamente.
  const handleDeleteMessage = useCallback(async (msg) => {
    if (DEBUG) console.log('handleDeleteMessage: Iniciando eliminación de mensaje:', msg.id);
    
    // Optimistic update: agregar ID a deletedMessageIds
    // Esto hará que el mensaje se muestre como eliminado inmediatamente
    setDeletedMessageIds(prev => new Set([...prev, msg.id]));
    
    try {
      // Llamar al servicio para eliminar el mensaje en el servidor
      await chatService.deleteMessage(msg.id);
      if (DEBUG) console.log('handleDeleteMessage: Mensaje eliminado exitosamente en servidor');
      
      // El mensaje se actualizará automáticamente cuando llegue desde el servidor
      // con msg_type 0, así que podemos limpiar el ID de deletedMessageIds
      // después de un breve delay para permitir que el servidor actualice
      
    } catch (error) {
      console.error('handleDeleteMessage: Error al eliminar mensaje:', error);
      
      // Revertir optimistic update en caso de error
      setDeletedMessageIds(prev => {
        const newSet = new Set(prev);
        newSet.delete(msg.id);
        return newSet;
      });
      
      // Mostrar alerta con el error
      alert('Error al eliminar mensaje. Inténtalo de nuevo.');
    }
  }, []);

  // Renderizado de grupos de mensajes
  const renderMessageGroup = useCallback((grupo, index) => {
    const DEBUG = process.env.NODE_ENV === 'development';

    if (DEBUG) {
      console.log('=== ChatRoomScreen: RENDERIZANDO GRUPO DE MENSAJES ===');
      console.log('ChatRoomScreen renderMessageGroup: Grupo index:', index);
      console.log('ChatRoomScreen renderMessageGroup: Fecha:', grupo.date_trunc);
      console.log('ChatRoomScreen renderMessageGroup: Cantidad mensajes:', grupo.mensajes_list?.length || 0);
      console.log('ChatRoomScreen renderMessageGroup: Mensajes IDs:', grupo.mensajes_list?.map(m => m.id) || []);
      console.log('ChatRoomScreen renderMessageGroup: Timestamp:', new Date().toISOString());
    }

    const mensajesOrdenados = sortMessages(grupo.mensajes_list);

    if (DEBUG) {
      console.log('ChatRoomScreen renderMessageGroup: Mensajes ordenados:', mensajesOrdenados.length);
      console.log('ChatRoomScreen renderMessageGroup: Mensajes ordenados IDs:', mensajesOrdenados.map(m => m.id));
    }

    return (
      <MemoizedMessageGroup
        key={`${grupo.date_trunc}-${index}`}
        grupo={{ ...grupo, current_user_id: user?.id }}
        mensajesOrdenados={mensajesOrdenados}
        scrollBottom={scrollBottom}
        setRespondTo={setRespondTo}
        focusearInput={focusearInput}
        markAsRead={markAsRead}
        onDeleteMessage={handleDeleteMessage}
        selectionMode={selectionMode}
        selectedImages={selectedChatImages}
        onImageSelect={handleChatImageSelect}
      />
    );
  }, [scrollBottom, setRespondTo, focusearInput, sortMessages, markAsRead, user?.id, handleDeleteMessage, selectionMode, selectedChatImages, handleChatImageSelect]);

  // Efecto para manejar el estado de bloqueo
  // Muestra alerta y redirige cuando el usuario es bloqueado
  useEffect(() => {
    if (isBlocked) {
      if (DEBUG) console.log('ChatRoomScreen: Usuario bloqueado detectado, mostrando alerta');
      alert('Esta persona ya no está disponible');
      navigate('/chats');
    }
  }, [isBlocked, navigate]);

  // Log para monitorear cambios en combinedMessages
  const prevCombinedMessagesRef = useRef(null);
  
  // Efecto para scrollear abajo y detectar nuevos mensajes
  useEffect(() => {    
    // Log para monitorear cambios en combinedMessages (dentro del useEffect)
    if (DEBUG) {
      const currentTotal = combinedMessages?.reduce((total, grupo) => total + (grupo.mensajes_list?.length || 0), 0) || 0;
      const prevTotal = prevCombinedMessagesRef.current?.reduce((total, grupo) => total + (grupo.mensajes_list?.length || 0), 0) || 0;
      
      if (currentTotal !== prevTotal) {
        console.log('🔄 ChatRoomScreen: combinedMessages CAMBIÓ');
        console.log('ChatRoomScreen: Anterior total mensajes:', prevTotal);
        console.log('ChatRoomScreen: Nuevo total mensajes:', currentTotal);
        console.log('ChatRoomScreen: Cambio:', currentTotal - prevTotal);
        console.log('ChatRoomScreen: Timestamp:', new Date().toISOString());
      }
      
      prevCombinedMessagesRef.current = combinedMessages;
      
      // Log siempre que se dispare el efecto
      console.log('🔔 ChatRoomScreen: EFECTO DE MENSAJES COMBINADOS DISPARADO');
      console.log('ChatRoomScreen useEffect: combinedMessages existe:', !!combinedMessages);
      console.log('ChatRoomScreen useEffect: combinedMessages.length:', combinedMessages?.length || 0);
      console.log('ChatRoomScreen useEffect: Timestamp:', new Date().toISOString());
    }
    
    if (combinedMessages && combinedMessages.length > 0) {
      if (DEBUG) {
        console.log('=== ChatRoomScreen: PROCESANDO MENSAJES COMBINADOS ===');
        console.log('ChatRoomScreen useEffect: Total grupos:', combinedMessages.length);
        console.log('ChatRoomScreen useEffect: Total mensajes:', combinedMessages.reduce((total, grupo) => total + (grupo.mensajes_list?.length || 0), 0));
        console.log('ChatRoomScreen useEffect: Último mensaje:', {
          id: combinedMessages[combinedMessages.length - 1]?.mensajes_list?.[combinedMessages[combinedMessages.length - 1]?.mensajes_list?.length - 1]?.id,
          body: combinedMessages[combinedMessages.length - 1]?.mensajes_list?.[combinedMessages[combinedMessages.length - 1]?.mensajes_list?.length - 1]?.body?.substring(0, 30),
          remitente: combinedMessages[combinedMessages.length - 1]?.mensajes_list?.[combinedMessages[combinedMessages.length - 1]?.mensajes_list?.length - 1]?.remitente
        });
        console.log('ChatRoomScreen useEffect: Grupos:', combinedMessages.map(g => ({
          fecha: g.date_trunc,
          cantidad: g.mensajes_list?.length || 0,
          mensajes_ids: g.mensajes_list?.map(m => ({ id: m.id, remitente: m.remitente, body: m.body?.substring(0, 30) }))
        })));
        console.log('ChatRoomScreen useEffect: Timestamp:', new Date().toISOString());
      }
      
      // Scrollear al final con pequeño delay
      const scrollTimeout = setTimeout(() => scrollBottom(), 100);

      // Detectar mensajes nuevos del otro usuario
      const currentMessageIds = new Set();
      let hasNewMessageFromOtherUser = false;
      let newMessagesCount = 0;

      combinedMessages.forEach(grupo => {
        grupo.mensajes_list.forEach(mensaje => {
          // Ignorar mensajes optimistas para detección de nuevos mensajes
          if (mensaje.is_optimistic) return;

          currentMessageIds.add(mensaje.id);

          // Marcar mensajes no leídos del otro usuario
          if (!messagesReadRef.current.has(mensaje.id) &&
              mensaje.remitente !== user?.id &&
              !mensaje.is_leido) {
            if (DEBUG) {
              console.log('ChatRoomScreen useEffect: Marcando mensaje como leído:', mensaje.id);
            }
            markAsRead(mensaje.id);
            messagesReadRef.current.add(mensaje.id);
          }

          // Si no existía antes y es del otro usuario, reproducir sonido
          if (!lastMessageIds.current.has(mensaje.id) &&
              mensaje.remitente !== user?.id) {
            hasNewMessageFromOtherUser = true;
            newMessagesCount++;
            // Solo loguear si no es el primer render (evita loguear historial completo)
            if (!isFirstRender.current && DEBUG) {
              console.log('useEffect: Nuevo mensaje detectado de otro usuario:', {
                id: mensaje.id,
                body: mensaje.body?.substring(0, 30),
                remitente: mensaje.remitente
              });
            }
          }
        });
      });

      // Reproducir sonido de recepción solo si hay mensajes nuevos del otro usuario
      if (!isFirstRender.current && hasNewMessageFromOtherUser) {
        if (DEBUG) console.log(`useEffect: Detectados ${newMessagesCount} mensajes nuevos del otro usuario - reproduciendo sonido`);

        try {
          playReceiveSound();
          if (DEBUG) console.log('useEffect: Sonido de recepción ejecutado');
        } catch (error) {
          if (DEBUG) console.error('useEffect: Error al reproducir sonido de recepción:', error);
        }
      } else {
        if (DEBUG) {
          console.log('useEffect: No hay mensajes nuevos del otro usuario o es primer render');
          console.log('useEffect: hasNewMessageFromOtherUser:', hasNewMessageFromOtherUser);
          console.log('useEffect: newMessagesCount:', newMessagesCount);
          console.log('useEffect: isFirstRender.current:', isFirstRender.current);
          console.log('useEffect: lastMessageIds.current.size:', lastMessageIds.current.size);
          console.log('useEffect: currentMessageIds.size:', currentMessageIds.size);
        }
        isFirstRender.current = false;
      }

      // Actualizar referencia de mensajes actuales
      lastMessageIds.current = currentMessageIds;

      return () => clearTimeout(scrollTimeout);
    }
  }, [combinedMessages, user?.id, playReceiveSound, scrollBottom, markAsRead]);

  // Efecto de cleanup para procesar batch de mensajes leídos al salir
  useEffect(() => {
    return () => {
      if (DEBUG) console.log('ChatRoomScreen: Cleanup - procesando batch de mensajes leídos');
      cleanup();
    };
  }, [cleanup]);

  // Redirige si falta información necesaria (solo si no está cargando)
  useEffect(() => {
    if (!sessionLoading && (!memoizedChatroomId || !memoizedFriendId || !user?.id)) {
      if (DEBUG) console.log('ChatRoomScreen: Faltan datos necesarios, redirigiendo a /chats');
      navigate('/chats');
    }
  }, [sessionLoading, memoizedChatroomId, memoizedFriendId, user?.id, navigate]);

  // Logs de renderizado principal
  const DEBUG = process.env.NODE_ENV === 'development';

  // Referencia para rastrear cambios en valores que causan re-renders
  const lastRenderValuesRef = useRef({});

  if (DEBUG) {
    const currentValues = {
      chatroom_id: memoizedChatroomId,
      friendId: memoizedFriendId,
      user_id: user?.id,
      combinedMessages_length: combinedMessages?.length || 0,
      selectionMode,
      selectedChatImages_length: selectedChatImages.length,
      modalVisible,
      modalFriendActionVisible,
      messageText,
      isBlocked
    };

    // Detectar qué cambió desde el último render
    const changedKeys = [];
    Object.keys(currentValues).forEach(key => {
      if (lastRenderValuesRef.current[key] !== currentValues[key]) {
        changedKeys.push(key);
      }
    });

    if (changedKeys.length > 0) {
      console.log('=== ChatRoomScreen: RENDERIZADO PRINCIPAL ===');
      console.log('ChatRoomScreen render: Valores que cambiaron:', changedKeys);
      console.log('ChatRoomScreen render: Valores actuales:', currentValues);
      console.log('ChatRoomScreen render: Timestamp:', new Date().toISOString());
    }

    lastRenderValuesRef.current = currentValues;
  }
  
  // No renderiza si faltan datos críticos (si está cargando, muestra loading)
  if (!memoizedChatroomId || !memoizedFriendId) {
    if (DEBUG) {
      console.log('ChatRoomScreen render: Mostrando loading - faltan datos críticos');
    }
    return (
      <div className="min-h-screen bg-neutral-900 flex items-center justify-center">
        <p className="text-white">Cargando...</p>
      </div>
    );
  }

  // Si la sesión está cargando pero no hay user.id, esperar
  if (sessionLoading && !user?.id) {
    if (DEBUG) {
      console.log('ChatRoomScreen render: Mostrando loading de sesión');
    }
    return (
      <div className="min-h-screen bg-neutral-900 flex items-center justify-center">
        <p className="text-white">Cargando sesión...</p>
      </div>
    );
  }

  return (
  <div>

    <div className='flex flex-col h-screen'>
      {/* Header */}
      <div className="h-20 sticky top-0 z-10 p-2 flex items-center justify-between bg-neutral-900 border-b border-neutral-800">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate('/chats')}
            className="p-2 hover:bg-neutral-800 rounded-lg transition-colors"
          >
            <FiArrowLeft size={24} color="white" />
          </button>
          <img
            src={avatar || defaultImage}
            alt={destinatario?.full_name}
            className="w-11 h-11 rounded-lg object-cover cursor-pointer"
            onClick={() => navigate(`/profile/${friendId}`)}
          />
          <h2 className="text-white text-base">{destinatario?.full_name}</h2>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setModalFriendActionVisible(true)}
            className="p-2 hover:bg-neutral-800 rounded-lg transition-colors"
          >
            <FiMoreVertical size={24} color="white" />
          </button>
        </div>
      </div>

      {/* Barra de selección de imágenes del chat */}
      {selectionMode && selectedChatImages.length > 0 && (
        <div className="bg-neutral-800 p-3 flex items-center justify-between border-b border-neutral-700">
          <p className="text-white text-sm">
            {selectedChatImages.length} imagen{selectedChatImages.length > 1 ? 'es' : ''} seleccionada{selectedChatImages.length > 1 ? 's' : ''}
          </p>
          <div className="flex gap-2">
            <button
              onClick={() => setSelectedChatImages([])}
              className="px-3 py-1 bg-neutral-700 text-white rounded-lg hover:bg-neutral-600 transition-colors text-sm"
            >
              Cancelar
            </button>
            <button
              onClick={() => {
                // Enviar directamente las imágenes seleccionadas del chat
                const imagesToSend = selectedChatImages.map(img => ({
                  img_id: img.img_id || img.id,
                  img_url: img.img_url
                }));
                setSelectionList(imagesToSend);
                setSelectionMode(false);
                setSelectedChatImages([]);
                shareImagesWithDuration();
              }}
              className="px-3 py-1 bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition-colors text-sm"
            >
              Enviar
            </button>
          </div>
        </div>
      )}

      {/* Lista de mensajes */}
      <div ref={messagesContainerRef} className="flex-1 overflow-y-auto py-4">
        <MessageList 
          combinedMessages={combinedMessages} 
          renderMessageGroup={renderMessageGroup} 
          isLoading={isLoading} 
        />
      </div>

      {/* Input de mensaje */}
      <div className="sticky bottom-0 bg-neutral-900">
        {respondTo && <RespondToComponent msg={respondTo} setRespondTo={setRespondTo} />}

        <InputChatBox
          destinatario={destinatario}
          messageText={messageText}
          setMessageText={setMessageText}
          maxLength={300}
          multiline={false}
          textInputRef={textInputRef}
          onPress={() => sendMessage(mensajitoBase)}
          actionComponent={<ActionMessageComponent setModalVisible={setModalVisible} />}
        />
      </div>

    </div>

      {/* Modal de adjuntos - Drawer */}
      {modalVisible && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex md:justify-end">
          {/* Drawer container */}
          <div className="bg-gradient-to-br from-neutral-800 to-neutral-900 w-full h-full md:max-w-2xl md:shadow-2xl md:h-[100dvh] md:h-auto overflow-hidden flex flex-col">
            {/* Header */}
            <div className="p-4 border-b border-neutral-700/50 bg-neutral-800/50 flex-shrink-0">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div>
                    <h3 className="text-white text-xl font-semibold">Galería de imágenes</h3>
                    <p className="text-neutral-300 text-sm">Selecciona las imágenes que deseas compartir</p>
                  </div>
                </div>
                <button
                  onClick={() => setModalVisible(false)}
                  className="w-10 h-10 rounded-xl bg-neutral-700/50 hover:bg-neutral-700 flex items-center justify-center transition-all hover:scale-105 flex-shrink-0"
                >
                  <FiX size={20} className="text-white" />
                </button>
              </div>
            </div>

            {/* Contenido del grid */}
            <div className="flex-1 p-4">
              <PermanentAlbums
                indiscreto={true}
                selectionList={selectionList}
                setSelectionList={setSelectionList}
                selectionModeText="Selecciona imágenes para enviar"
                hideDeleteButton={true}
                // hideUploadButton={true}
                disableLongPress={false}
                isSelectable={true}
                selectableAction={shareImagesWithDuration}
              />
            </div>

            {/* Barra de selección */}
              <div className="border-t border-neutral-700">
                <div className="h-16 px-4 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <span className="text-white font-medium">
                      {selectionList.length} imagen{selectionList.length === 1 ? '' : 'es'} seleccionada{selectionList.length === 1 ? '' : 's'}
                    </span>
                  </div>
                  {selectionList.length > 0 && <div className="flex gap-2">
                    <button
                      onClick={() => setSelectionList([])}
                      className="px-4 py-2 bg-neutral-700 text-white rounded-lg hover:bg-neutral-600 transition-all hover:scale-105 font-medium text-sm"
                    >
                      Limpiar
                    </button>
                    <button
                      onClick={shareImagesWithDuration}
                      className="px-5 py-2 bg-gradient-to-r from-primary-500 to-primary-600 text-white rounded-lg hover:from-primary-600 hover:to-primary-700 transition-all hover:scale-105 font-medium text-sm shadow-lg shadow-primary-500/25"
                    >
                      Enviar
                    </button>
                  </div>}
                </div>
              </div>
          </div>
        </div>
      )}

      {/* Modal de acciones de amigo */}
      {modalFriendActionVisible && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-neutral-800 rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-white text-lg mb-4 text-center">¿Pasa algo con este perfil?</h3>
            <div className="space-y-4">
              <button
                className="w-full bg-neutral-700 text-white p-4 rounded-lg hover:bg-neutral-600 transition-colors flex items-center justify-between"
                onClick={createImpeachment}
              >
                <span>Reportar</span>
                <span>🚩</span>
              </button>
              <button
                className="w-full bg-neutral-700 text-white p-4 rounded-lg hover:bg-neutral-600 transition-colors flex items-center justify-between"
                onClick={confirmBlockPerson}
              >
                <span>Bloquear</span>
                <span>🚫</span>
              </button>
            </div>
            <button
              className="w-full mt-4 bg-neutral-600 text-white p-4 rounded-lg hover:bg-neutral-500 transition-colors"
              onClick={handleCloseModal}
            >
              Cancelar
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// Memoizar el componente para evitar re-renders innecesarios
const OptimizedChatRoomScreen = memo(ChatRoomScreen, (prevProps, nextProps) => {
  // ChatRoomScreen no recibe props, así que siempre debería ser el mismo
  // El memo aquí es para prevenir re-renders desde el padre
  return true;
});
export default OptimizedChatRoomScreen;

// Componente memoizado para la lista de mensajes
const MessageList = memo(({ combinedMessages, renderMessageGroup, isLoading }) => {
  if (isLoading) {
    return (
      <div className="h-full flex items-center justify-center">
        <BiLoaderAlt className="animate-spin text-white ml-2" size={20} />
        <p className="text-white ml-2">Cargando mensajes...</p>
      </div>
    );
  }

  if (!combinedMessages || combinedMessages.length === 0) {
    return (
      <div className="flex items-center justify-center h-full">
        <p className="text-white">No hay mensajes aún. ¡Inicia la conversación!</p>
      </div>
    );
  }

  return (
    <>
      {combinedMessages.map((grupo, index) => renderMessageGroup(grupo, index))}
    </>
  );
}, (prevProps, nextProps) => {
  // Solo re-renderizar si combinedMessages cambió realmente
  // Ignorar cambios en renderMessageGroup o isLoading
  return prevProps.combinedMessages === nextProps.combinedMessages;
});

// Componente para acción de mensaje (clip)
function ActionMessageComponent({ setModalVisible }) {
  return (
    <button
      className="w-11 h-11 flex items-center justify-center rounded-full bg-transparent hover:bg-neutral-700 transition-colors"
      onClick={() => setModalVisible(true)}
    >
      <FiPaperclip size={24} color="white" />
    </button>
  );
}

// Componente para mostrar respuesta
function RespondToComponent({ msg, setRespondTo }) {
  const labelType = [
    'Se eliminó el mensaje',
    msg.body,
    'Ubicación',
    'Imagen',
    msg.body,
    msg.shared_albums?.name || 'Álbum compartido',
    'Imagen compartida',
  ];

  return (
    <div className="flex justify-between items-center p-3 bg-neutral-900 border-t border-neutral-700">
      <div className="bg-neutral-800 rounded-lg flex-1 p-3 opacity-50">
        <p className="text-sm italic text-white">{labelType[msg.msg_type]}</p>
      </div>
      <button
        className="rounded-full p-1 hover:bg-neutral-700 transition-colors ml-2"
        onClick={() => setRespondTo(null)}
      >
        <FiX size={20} color="white" />
      </button>
    </div>
  );
}
